import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Card, Button, Badge } from '../../../components/PageStyles';
import { apiClient } from '../../../utils/apiClient';
import { activityTracker } from '../../../services/activityTracker';
import { BaseModal } from '../../../components/shared/BaseModal';
import { notify } from '../../../utils/notifications';
import { FiAlertCircle, FiClock, FiCheckCircle, FiCheck, FiUserPlus } from 'react-icons/fi';
import { FaTag } from 'react-icons/fa';
import PreRegistrationModal from '../../../components/PreRegistrationModal';
import { useAuthStore } from '../../../stores/authStore';
// Styled Components
const InquiriesGrid = styled.div `
  display: grid;
  gap: 20px;
  margin-top: 24px;
`;
const InquiryCard = styled(Card) `
  padding: 24px;
  position: relative;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: var(--shadow-lg);
    border-color: var(--accent-gold);
  }
`;
const InquiryHeader = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;
const InquiryInfo = styled.div `
  flex: 1;
`;
const InquiryName = styled.h3 `
  font-family: 'Playfair Display', 'Adamina', serif;
  font-size: 20px;
  font-weight: 500;
  color: var(--text-primary);
  margin: 0 0 8px 0;
`;
const InquiryEmail = styled.a `
  font-size: 14px;
  color: var(--accent-gold);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;
const InquiryMeta = styled.div `
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 8px;
`;
const MetaItem = styled.span `
  font-size: 12px;
  color: var(--text-tertiary);
`;
const InquiryMessage = styled.div `
  margin: 16px 0;
  padding: 16px;
  background: var(--bg-tertiary);
  border-left: 3px solid var(--accent-gold);
  font-size: 14px;
  line-height: 1.6;
  color: var(--text-secondary);
`;
const InquiryActions = styled.div `
  display: flex;
  gap: 12px;
  margin-top: 16px;
`;
const FilterBar = styled.div `
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;
const FilterButton = styled.button `
  padding: 10px 20px;
  background: ${props => props.active ? 'var(--accent-gold)' : 'var(--bg-secondary)'};
  color: ${props => props.active ? '#FFFFFF' : 'var(--text-primary)'};
  border: 1px solid ${props => props.active ? 'var(--accent-gold)' : 'var(--border-color)'};
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--accent-gold);
    ${props => !props.active && `
      color: var(--accent-gold);
    `}
  }
`;
const EmptyState = styled.div `
  text-align: center;
  padding: 80px 20px;
  color: var(--text-tertiary);

  h3 {
    font-family: 'Playfair Display', 'Adamina', serif;
    font-size: 24px;
    color: var(--text-secondary);
    margin-bottom: 12px;
  }

  p {
    font-size: 14px;
  }
`;
const LoadingState = styled.div `
  text-align: center;
  padding: 60px 20px;
  color: var(--text-secondary);
  font-size: 16px;
`;
const ErrorState = styled.div `
  padding: 20px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 4px;
  color: #ef4444;
  text-align: center;
  margin-bottom: 24px;
`;
const StatusOption = styled.button `
  width: 100%;
  padding: 16px 20px;
  background: ${props => props.active ? 'var(--accent-gold)' : 'var(--bg-secondary)'};
  color: ${props => props.active ? '#FFFFFF' : 'var(--text-primary)'};
  border: 2px solid ${props => props.active ? 'var(--accent-gold)' : 'var(--border-color)'};
  border-radius: 8px;
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;

  &:hover {
    border-color: var(--accent-gold);
    ${props => !props.active && `
      background: var(--bg-tertiary);
    `}
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
const StatusLabel = styled.span `
  display: flex;
  align-items: center;
  gap: 8px;
`;
const StatusDescription = styled.div `
  font-size: 12px;
  font-weight: 400;
  opacity: 0.8;
  margin-top: 4px;
`;
const ModalActions = styled.div `
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid var(--border-color);
`;
const BadgeRow = styled.div `
  display: flex;
  gap: 8px;
  align-items: center;
`;
const TypeBadge = styled.span `
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${props => props.$variant === 'gold'
    ? 'rgba(153, 101, 21, 0.15)'
    : 'var(--bg-tertiary)'};
  color: ${props => props.$variant === 'gold'
    ? 'var(--accent-gold, #996515)'
    : 'var(--text-secondary)'};
  border: 1px solid ${props => props.$variant === 'gold'
    ? 'rgba(153, 101, 21, 0.3)'
    : 'var(--border-color)'};
`;
const DiamondInfoBanner = styled.div `
  margin: 12px 0;
  padding: 12px 16px;
  background: rgba(153, 101, 21, 0.06);
  border: 1px solid rgba(153, 101, 21, 0.2);
  border-left: 3px solid var(--accent-gold, #996515);
  font-size: 13px;
  color: var(--text-secondary);
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
`;
const DiamondSpec = styled.span `
  display: inline-flex;
  align-items: center;
  gap: 4px;

  strong {
    color: var(--text-primary);
  }
`;
const InquiryListTab = ({ inquiryTypeFilter }) => {
    const { user } = useAuthStore();
    const [inquiries, setInquiries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filter, setFilter] = useState('all');
    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const [selectedInquiry, setSelectedInquiry] = useState(null);
    const [newStatus, setNewStatus] = useState('');
    const [updating, setUpdating] = useState(false);
    const [preRegModalOpen, setPreRegModalOpen] = useState(false);
    const [preRegData, setPreRegData] = useState(null);
    useEffect(() => {
        fetchInquiries();
    }, [filter, inquiryTypeFilter]);
    const fetchInquiries = async () => {
        try {
            setLoading(true);
            setError('');
            const params = new URLSearchParams();
            if (filter !== 'all')
                params.append('status', filter);
            if (inquiryTypeFilter)
                params.append('inquiryType', inquiryTypeFilter);
            const queryString = params.toString();
            const endpoint = queryString
                ? `contact-inquiries/list?${queryString}`
                : 'contact-inquiries/list';
            const response = await apiClient.get(endpoint);
            if (response.ok && response.data?.success) {
                setInquiries(response.data.inquiries);
            }
            else {
                setError(response.error || 'Failed to load inquiries');
            }
        }
        catch (err) {
            setError('Network error. Please try again.');
        }
        finally {
            setLoading(false);
        }
    };
    const getStatusBadge = (status) => {
        const statusColors = {
            new: 'accent',
            in_progress: undefined,
            contacted: 'success',
            resolved: undefined
        };
        return statusColors[status];
    };
    const getStatusLabel = (status) => {
        const labels = {
            new: 'New',
            in_progress: 'In Progress',
            contacted: 'Contacted',
            resolved: 'Resolved'
        };
        return labels[status] || status;
    };
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).format(date);
    };
    const openStatusModal = (inquiry) => {
        setSelectedInquiry(inquiry);
        setNewStatus(inquiry.status);
        setStatusModalOpen(true);
    };
    const closeStatusModal = () => {
        setStatusModalOpen(false);
        setSelectedInquiry(null);
        setNewStatus('');
    };
    const updateInquiryStatus = async () => {
        if (!selectedInquiry || !newStatus)
            return;
        try {
            setUpdating(true);
            const response = await apiClient.put(`contact-inquiries/${selectedInquiry.id}`, { status: newStatus });
            if (response.ok && response.data?.success) {
                activityTracker.trackAdminAction('responded-to-inquiry', `Inquiry ${selectedInquiry.id} status changed to ${newStatus}`, { inquiryId: selectedInquiry.id, newStatus });
                notify.success('Status Updated', `Inquiry status changed to ${getStatusLabel(newStatus)}`);
                closeStatusModal();
                fetchInquiries();
            }
            else {
                notify.error('Update Failed', response.error || 'Failed to update inquiry status');
            }
        }
        catch (err) {
            notify.error('Update Failed', 'Network error. Please try again.');
        }
        finally {
            setUpdating(false);
        }
    };
    const statusOptions = [
        { value: 'new', label: 'New', description: 'Inquiry has not been reviewed yet', icon: FiAlertCircle },
        { value: 'in_progress', label: 'In Progress', description: 'Currently reviewing or drafting response', icon: FiClock },
        { value: 'contacted', label: 'Contacted', description: 'Response has been sent to the customer', icon: FiCheckCircle },
        { value: 'resolved', label: 'Resolved', description: 'Inquiry has been fully resolved', icon: FiCheck }
    ];
    const openPreRegistrationModal = (inquiry) => {
        const nameParts = inquiry.name.trim().split(/\s+/);
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        setPreRegData({
            email: inquiry.email,
            firstName: firstName,
            lastName: lastName,
            businessName: inquiry.businessUrl || ''
        });
        setPreRegModalOpen(true);
    };
    return (_jsxs(_Fragment, { children: [_jsxs(FilterBar, { children: [_jsx(FilterButton, { active: filter === 'all', onClick: () => setFilter('all'), children: "All" }), _jsx(FilterButton, { active: filter === 'new', onClick: () => setFilter('new'), children: "New" }), _jsx(FilterButton, { active: filter === 'in_progress', onClick: () => setFilter('in_progress'), children: "In Progress" }), _jsx(FilterButton, { active: filter === 'contacted', onClick: () => setFilter('contacted'), children: "Contacted" }), _jsx(FilterButton, { active: filter === 'resolved', onClick: () => setFilter('resolved'), children: "Resolved" })] }), error && (_jsx(ErrorState, { children: error })), loading ? (_jsx(LoadingState, { children: "Loading inquiries..." })) : inquiries.length === 0 ? (_jsxs(EmptyState, { children: [_jsx("h3", { children: "No Inquiries Found" }), _jsx("p", { children: "There are no submissions matching this filter." })] })) : (_jsx(InquiriesGrid, { children: inquiries.map((inquiry) => (_jsxs(InquiryCard, { children: [_jsxs(InquiryHeader, { children: [_jsxs(InquiryInfo, { children: [_jsx(InquiryName, { children: inquiry.name }), _jsx(InquiryEmail, { href: `mailto:${inquiry.email}`, children: inquiry.email }), _jsxs(InquiryMeta, { children: [_jsx(MetaItem, { children: formatDate(inquiry.createdAt) }), inquiry.businessUrl && (_jsx(MetaItem, { children: _jsx("a", { href: inquiry.businessUrl.startsWith('http') ? inquiry.businessUrl : `https://${inquiry.businessUrl}`, target: "_blank", rel: "noopener noreferrer", style: { color: 'var(--accent-gold)' }, children: inquiry.businessUrl }) })), inquiry.newsletter && (_jsx(MetaItem, { children: "Newsletter Signup" }))] })] }), _jsxs(BadgeRow, { children: [inquiry.inquiryType === 'price_inquiry' && (_jsxs(TypeBadge, { "$variant": "gold", children: [_jsx(FaTag, { size: 10 }), "Price Inquiry"] })), _jsx(Badge, { color: getStatusBadge(inquiry.status), children: getStatusLabel(inquiry.status) })] })] }), inquiry.diamond && (_jsxs(DiamondInfoBanner, { children: [_jsx(DiamondSpec, { children: _jsx("strong", { children: inquiry.diamond.shape }) }), _jsx(DiamondSpec, { children: _jsxs("strong", { children: [inquiry.diamond.caratWeight, "ct"] }) }), _jsxs(DiamondSpec, { children: [inquiry.diamond.color, " / ", inquiry.diamond.clarity] }), _jsx(DiamondSpec, { children: inquiry.diamond.lab }), inquiry.diamond.stockNumber && (_jsxs(DiamondSpec, { children: ["Stock #", inquiry.diamond.stockNumber] })), inquiry.diamond.certificateNumber && (_jsxs(DiamondSpec, { children: ["Cert #", inquiry.diamond.certificateNumber] }))] })), inquiry.message && (_jsx(InquiryMessage, { children: inquiry.message })), _jsxs(InquiryActions, { children: [!inquiry.inquiryType && (_jsxs(Button, { variant: "secondary", onClick: () => openPreRegistrationModal(inquiry), title: "Create a pre-registration link for this person", children: [_jsx(FiUserPlus, { style: { marginRight: '6px' } }), "Create Account"] })), _jsx(Button, { variant: "primary", onClick: () => window.location.href = `mailto:${inquiry.email}`, children: "Reply via Email" }), _jsx(Button, { variant: "secondary", onClick: () => openStatusModal(inquiry), children: "Update Status" })] })] }, inquiry.id))) })), selectedInquiry && (_jsx(BaseModal, { isOpen: statusModalOpen, onClose: closeStatusModal, title: "Update Inquiry Status", size: "md", children: _jsxs("div", { style: { padding: '8px 0' }, children: [_jsxs("p", { style: { color: 'var(--text-secondary)', marginBottom: '24px', fontSize: '14px' }, children: ["Update the status for ", _jsx("strong", { style: { color: 'var(--text-primary)' }, children: selectedInquiry.name }), "'s inquiry"] }), _jsx("div", { children: statusOptions.map((option) => {
                                const Icon = option.icon;
                                return (_jsxs(StatusOption, { active: newStatus === option.value, onClick: () => setNewStatus(option.value), disabled: updating, children: [_jsxs(StatusLabel, { children: [_jsx(Icon, { size: 20, style: { flexShrink: 0 } }), _jsxs("div", { children: [_jsx("div", { children: option.label }), _jsx(StatusDescription, { children: option.description })] })] }), newStatus === option.value && (_jsx(FiCheck, { size: 20 }))] }, option.value));
                            }) }), _jsxs(ModalActions, { children: [_jsx(Button, { variant: "secondary", onClick: closeStatusModal, disabled: updating, children: "Cancel" }), _jsx(Button, { variant: "primary", onClick: updateInquiryStatus, disabled: updating || newStatus === selectedInquiry.status, children: updating ? 'Updating...' : 'Update Status' })] })] }) })), user && (_jsx(PreRegistrationModal, { isOpen: preRegModalOpen, onClose: () => {
                    setPreRegModalOpen(false);
                    setPreRegData(null);
                }, currentUserRole: user.roleLevel, initialData: preRegData }))] }));
};
export default InquiryListTab;
